import { sanitizeContent, normalizeCSV, csvToFile } from 'src/util/csv';
import { CLOUD_FETCH_TIMEOUT_MS } from 'src/sections/UploadSurvey/constants';

export default class DropboxService {
  isValidDropboxUrl(url) {
    if (!url || typeof url !== 'string') {
      return false;
    }

    const patterns = [
      /dropbox\.com\//i,
      /dl\.dropboxusercontent\.com\//i,
    ];

    return patterns.some((pattern) => pattern.test(url));
  }

  convertToDirectDownloadUrl(url) {
    try {
      const urlObj = new URL(url);

      if (urlObj.hostname.includes('dropboxusercontent.com')) {
        return url;
      }

      urlObj.hostname = 'dl.dropboxusercontent.com';
      urlObj.searchParams.set('dl', '1');
      return urlObj.toString();
    } catch {
      return url;
    }
  }

  extractFileNameFromUrl(url) {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const segments = pathname.split('/').filter(Boolean);
      const last = segments[segments.length - 1];

      if (last && /\.\w{2,5}$/.test(last)) {
        return decodeURIComponent(last);
      }
    } catch {
      return null;
    }

    return null;
  }

  extractFileNameFromResponse(response, fallback = 'dropbox-import.csv') {
    const disposition = response.headers.get('content-disposition');
    if (disposition) {
      const utf8Match = disposition.match(/filename\*=UTF-8''(.+)/i);
      if (utf8Match) return decodeURIComponent(utf8Match[1]);

      const match = disposition.match(/filename="?([^";\n]+)"?/i);
      if (match) return match[1].trim();
    }

    try {
      const pathname = new URL(response.url).pathname;
      const segments = pathname.split('/').filter(Boolean);
      const last = segments[segments.length - 1];
      if (last && /\.\w{2,5}$/.test(last)) return decodeURIComponent(last);
    } catch {
      return fallback;
    }

    return fallback;
  }

  async fetchWithTimeout(url, options = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CLOUD_FETCH_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw new Error('Tempo limite excedido. Verifique sua conexão e tente novamente.');
      }

      throw error;
    }
  }

  isExcelFile(filename) {
    return /\.xlsx?$/i.test(filename);
  }

  isCsvFile(filename) {
    return /\.csv$/i.test(filename);
  }

  async excelToCSV(arrayBuffer) {
    try {
      const ExcelJS = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        throw new Error('Planilha vazia.');
      }

      const rows = [];
      worksheet.eachRow((row) => {
        const values = row.values;
        const cells = values.slice(1).map((cell) => {
          if (cell === null || cell === undefined) return '';
          if (typeof cell === 'object' && cell.text) return cell.text;
          if (typeof cell === 'object' && cell.result !== undefined) return String(cell.result);
          return String(cell);
        });
        rows.push(cells.join(';'));
      });

      return rows.join('\n');
    } catch (error) {
      if (error.message === 'Planilha vazia.') throw error;
      throw new Error('Erro ao processar arquivo Excel. Verifique se o arquivo não está corrompido.');
    }
  }

  async importFromUrl(url) {
    if (!this.isValidDropboxUrl(url)) {
      throw new Error('URL do Dropbox inválida. Use um link de compartilhamento do Dropbox.');
    }

    const downloadUrl = this.convertToDirectDownloadUrl(url);

    const response = await this.fetchWithTimeout(downloadUrl, { method: 'GET' });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('Sem permissão para acessar este arquivo. Verifique se o link é público.');
      }
      if (response.status === 404) {
        throw new Error('Arquivo não encontrado. Verifique se o link está correto.');
      }
      throw new Error(`Erro ao acessar arquivo: ${response.status}`);
    }

    const name = this.extractFileNameFromResponse(response)
      || this.extractFileNameFromUrl(url)
      || 'dropbox-import.csv';

    const arrayBuffer = await response.arrayBuffer();

    if (!this.isCsvFile(name) && !this.isExcelFile(name)) {
      throw new Error('Formato não suportado. Envie um arquivo CSV ou Excel (.xlsx).');
    }

    let csvContent;

    if (this.isExcelFile(name)) {
      csvContent = await this.excelToCSV(arrayBuffer);
    } else {
      const text = new TextDecoder('utf-8').decode(arrayBuffer);
      const sanitized = sanitizeContent(text);
      csvContent = normalizeCSV(sanitized);
    }

    const fileName = name.replace(/\.xlsx?$/i, '.csv');
    const file = csvToFile(csvContent, fileName);

    return { file, fileName };
  }
}

export const dropboxService = new DropboxService();
